import { FC, useMemo, useState } from 'react'
import pluralize from 'pluralize'
import { Box, Button, CircularProgress, Grid, InputAdornment, TextField, Typography } from '@mui/material'
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium'
import { DateRange } from '@mui/x-date-pickers-pro'
import { startOfDay, endOfDay, formatISO } from 'date-fns'
import { useSensorLocationReadout, LocationReadingType } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { detectLocationMovements, locationSeverityBands, LocationMovementResult } from 'src/helpers'
import { DateRangePresetPicker, getDateRangePresetValue } from 'src/components/ui'
import { SensorLocationMovementColumns, locationSeverityPalette } from 'src/components/columns/sensorMovementDetection'

interface Props {
  sensorUniqueId: string
  locationType: LocationReadingType
}

const DEFAULT_LOCATION_THRESHOLD_METERS = 50

const getColumns = () => [
  SensorLocationMovementColumns.Time,
  SensorLocationMovementColumns.Distance,
  SensorLocationMovementColumns.Severity,
  SensorLocationMovementColumns.Links
]

const formatBandRange = ({ from, to }: { from: number; to: number }): string => {
  if (from === 0) return `≤${to} m`
  if (to === Infinity) return `>${from} m`
  return `${from}–${to} m`
}

const ResultsSummary: FC<{ count: number }> = ({ count }) => (
  <Grid container spacing={2} alignItems="center" mb={1}>
    <Grid item>
      <Typography variant="caption" color="text.secondary">
        {pluralize('movement', count, true)} found
      </Typography>
    </Grid>
    {locationSeverityBands.map((band) => (
      <Grid item key={band.severity} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: locationSeverityPalette[band.severity].background, border: '1px solid', borderColor: locationSeverityPalette[band.severity].text }} />
        <Typography variant="caption" sx={{ color: locationSeverityPalette[band.severity].text }}>
          {band.label} {formatBandRange(band)}
        </Typography>
      </Grid>
    ))}
  </Grid>
)

export const SensorLocationMovementPanel: FC<Props> = ({ sensorUniqueId, locationType }) => {
  const apiRef = useGridApiRef()
  const addPopupMessage = useAddPopupMessage()
  const [dateRange, setDateRange] = useState<DateRange<Date>>(getDateRangePresetValue('thisWeek'))
  const [dateFrom, dateTo] = dateRange
  const [threshold, setThreshold] = useState<number | ''>(DEFAULT_LOCATION_THRESHOLD_METERS)

  const readoutRequest = useSensorLocationReadout({
    sensorUniqueId,
    locationType,
    options: {
      onError: ({ message: text }) => addPopupMessage({ text, type: 'error' })
    }
  })

  const rows: LocationMovementResult[] = useMemo(() => detectLocationMovements(readoutRequest.data || [], Number(threshold) || 0), [readoutRequest.data, threshold])

  const canCheck = Boolean(dateFrom && dateTo && dateFrom <= dateTo && Number(threshold) > 0)

  const handleCheck = () => {
    if (!dateFrom || !dateTo) return
    readoutRequest.mutate({
      timeFrom: formatISO(startOfDay(dateFrom)),
      timeTo: formatISO(endOfDay(dateTo))
    })
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-end" mb={2}>
        <Grid item>
          <DateRangePresetPicker value={dateRange} onChange={setDateRange} initialPreset="thisWeek" />
        </Grid>
        <Grid item>
          <TextField
            label="Threshold"
            type="number"
            value={threshold}
            onChange={({ target: { value } }) => setThreshold(value === '' ? '' : Number(value))}
            InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
            inputProps={{ min: 0, step: 1 }}
            sx={{ width: 110 }}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" size="large" onClick={handleCheck} sx={{ minWidth: 180 }} disabled={readoutRequest.isLoading || !canCheck}>
            {readoutRequest.isLoading ? <CircularProgress size={26} color="inherit" /> : 'Check movement'}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="text" size="large" onClick={() => readoutRequest.reset()} disabled={!rows.length}>
            Hide
          </Button>
        </Grid>
      </Grid>
      {readoutRequest.isSuccess && !rows.length ? (
        <Typography variant="body2" color="text.secondary">
          No movement detected in the selected period
        </Typography>
      ) : null}
      {rows.length ? (
        <>
          <ResultsSummary count={rows.length} />
          <DataGridPremium<LocationMovementResult>
            apiRef={apiRef}
            autoHeight
            disableRowSelectionOnClick
            rows={rows}
            columns={getColumns()}
            loading={readoutRequest.isLoading}
            getRowId={(row) => row.id}
            hideFooter={rows.length <= 100}
            rowThreshold={2}
            columnThreshold={2}
            localeText={{
              columnMenuSortAsc: 'Sort A-Z',
              columnMenuSortDesc: 'Sort Z-A'
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                paddingLeft: '5px',
                overflow: 'visible'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              }
            }}
          />
        </>
      ) : null}
    </Box>
  )
}
